import { Link } from 'react-router-dom-v5-compat'
import Select from 'react-select'

import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Paper from '@mui/material/Paper'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useTheme } from '@mui/material/styles'

import { SELLER_FLEET } from '~/constants'
import { useAppSelector } from '~/store'
import { getPath } from '~/utils/getPath'
import { hasPermission } from '~/utils/hasPermission'
import CancelButton from '../../containers/cancel-button'
import SaleCancelConfirmation from '../../containers/sale-cancel-confirmation'
import { SALE_FORM_MODES } from '../sale'
import ShopSelect from './shop-selector'

export default function SaleHeader({
    sale,
    saleHasChanged,
    mode,
    stores,
    submitting,
    saleStatusMap,
    onShopChange,
    onStatusChange,
    onSaleIdChange,
    onSaveClick,
    onSaleCanceled,
    onSubmitCancelConfirmation,
}) {
    const isAdd = mode === SALE_FORM_MODES.add
    const isManualSale = !!sale.can_edit_items
    const saleListPath = getPath('sales')
    const canEditSaleStatus = sale.can_edit_status || sale.shipping_provider === SELLER_FLEET
    const showStatusSelect = canEditSaleStatus && Object.keys(saleStatusMap).length > 1
    const showCancelButton =
        !isAdd && ['unpaid', 'pending', 'waiting_for_stock'].indexOf(sale.status) > -1
    const showCreditNoteButton = !isAdd && sale.sale_returns?.length > 0
    const showCancelConfirmation = !isAdd && sale.status === 'in_cancel'
    const enableSaveButton = isAdd || saleHasChanged
    const permissions = useAppSelector((state) => state.initial.user.permissions)

    return (
        <>
            <Button component={Link} to={saleListPath} color="secondary">
                <ArrowBackIcon />
                {gettext('Orders')}
            </Button>

            {showCancelConfirmation && (
                <Paper sx={{ mb: 1, py: 0.75, px: 1.5 }}>
                    <SaleCancelConfirmation
                        saleId={sale.id}
                        onSubmitConfirmation={onSubmitCancelConfirmation}
                    />
                </Paper>
            )}

            <Paper
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 2,
                    px: 2.5,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {isAdd && isManualSale && (
                        <TextField
                            variant="outlined"
                            margin="none"
                            size="small"
                            label={gettext('Sale ID (optional)')}
                            value={sale.sale_id ?? ''}
                            placeholder={gettext('Sale ID (optional)')}
                            inputProps={{ maxLength: 30 }} // from sales/models.py Sale.sale_id
                            onChange={(e) => onSaleIdChange(e.target.value)}
                        />
                    )}
                    {!isAdd && sale.sale_id && (
                        <Typography variant="subtitle2">
                            {gettext('Sale ID: ')}
                            <Typography
                                variant="subtitle2"
                                component="span"
                                style={{ fontWeight: 400 }}
                            >
                                {sale.sale_id}
                            </Typography>
                        </Typography>
                    )}
                    {isAdd && (
                        <ShopSelect
                            data={stores}
                            dataIsStores={true}
                            shopId={sale?.shop?.id}
                            onChange={onShopChange}
                        />
                    )}
                    {!isAdd && sale?.shop?.channel && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <img
                                src={sale.shop.channel.channel_square_icon_url}
                                alt={sale.shop.channel.name}
                                height={20}
                                width={20}
                            />
                            <Typography component="span" variant="subtitle2" color="textSecondary">
                                {sale.shop.shop_name}
                            </Typography>
                        </Box>
                    )}
                    {showStatusSelect ? (
                        <SaleStatusSelector
                            statuses={saleStatusMap}
                            value={sale.status}
                            onChange={onStatusChange}
                        />
                    ) : (
                        <Chip
                            color="secondary"
                            size="small"
                            variant="outlined"
                            hidden={!sale.status_display_name}
                            label={sale.status_display_name}
                            sx={{
                                fontWeight: 'normal',
                                '&[hidden]': {
                                    display: 'none',
                                },
                            }}
                        />
                    )}
                </Box>

                <Box sx={{ flexShrink: 0 }}>
                    {showCreditNoteButton && (
                        <Button
                            color="secondary"
                            href={`/sales/${sale.id}/credit-note`}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {gettext('Credit note')}
                        </Button>
                    )}

                    {hasPermission(permissions, 'module.chat') && !!sale.channel_customer_id && (
                        <Button
                            color="secondary"
                            component={Link}
                            to={() => {
                                const urlParams = new URLSearchParams({
                                    channel_customer_id: sale.channel_customer_id,
                                })

                                return `${getPath('chat')}?${urlParams.toString()}`
                            }}
                        >
                            {gettext('Message buyer')}
                        </Button>
                    )}

                    {!isAdd && (
                        <Button
                            color="secondary"
                            href={`/sales/${sale.id}/invoice`}
                            target="_blank"
                        >
                            {gettext('Invoice')}
                        </Button>
                    )}

                    {submitting && (
                        <CircularProgress
                            color="secondary"
                            size={16}
                            sx={{ verticalAlign: 'middle', mr: 1 }}
                        />
                    )}

                    {showCancelButton && (
                        <CancelButton
                            saleId={sale.id}
                            btnProps={{ disabled: submitting }}
                            onSaleCanceled={onSaleCanceled}
                        />
                    )}

                    {isAdd && (
                        <Button component={Link} to={saleListPath} disabled={submitting}>
                            {gettext('Discard')}
                        </Button>
                    )}

                    <Button
                        color="secondary"
                        variant="contained"
                        sx={{ ml: 1 }}
                        disabled={!enableSaveButton || submitting}
                        onClick={onSaveClick}
                    >
                        {gettext('Save')}
                    </Button>
                </Box>
            </Paper>
        </>
    )
}

function SaleStatusSelector({ statuses, value, onChange, placeholder = gettext('Sale status') }) {
    const theme = useTheme()

    const selectStyles = {
        control: (provided, state) => ({
            ...provided,
            minWidth: 220,
            borderColor: state.isFocused ? theme.palette.primary.main : 'hsl(0,0%,80%)',
            boxShadow: state.isFocused ? theme.palette.primary.main : 'none',
            '&:hover': {
                borderColor: theme.palette.primary.main,
            },
            color: theme.palette.grey[700],
        }),
        dropdownIndicator: (provided) => ({
            ...provided,
            padding: 4,
        }),
        clearIndicator: (provided) => ({
            ...provided,
            padding: 4,
        }),
        valueContainer: (provided) => ({
            ...provided,
            padding: '0px 6px',
            color: theme.palette.grey[700],
        }),
        menu: (provided) => ({
            ...provided,
            color: theme.palette.grey[700],
        }),
        input: (provided) => ({
            ...provided,
            margin: 0,
            padding: 0,
        }),
        placeholder: (provided) => ({
            ...provided,
            paddingTop: 1,
        }),
    }

    const statusOptions = Object.entries(statuses).map(([status, text]) => ({
        label: text,
        value: status,
    }))

    return (
        <Select
            styles={selectStyles}
            onChange={(selectedOption) => onChange(selectedOption.value)}
            placeholder={placeholder}
            isSearchable={false}
            isClearable={false}
            options={statusOptions}
            value={statusOptions.find((item) => item.value === value)}
        />
    )
}
